import NotFound from "./NotFound";
import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import { Phone, CalendarCheck, ShieldCheck, Clock, MapPin, Check, Car, Bike, Truck, Caravan, CarFront, Route } from "lucide-react";
import SubLayout from "@/components/site/SubLayout";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import CtaBlock from "@/components/site/CtaBlock";
import Seo from "@/seo/Seo";
import { breadcrumbSchema, localBusinessSchema, faqSchema, serviceSchema } from "@/seo/schemas";
import { SITE } from "@/data/site";
import { CITIES, cityBySlug } from "@/data/cities";

const VEHICLE_LINKS = [
  { icon: Car, label: "Cars & SUVs", to: "/services/california-vin-verification" },
  { icon: Route, label: "Out-of-State", to: "/services/out-of-state-vin-verification" },
  { icon: Bike, label: "Motorcycles", to: "/services/motorcycle-vin-verification" },
  { icon: Truck, label: "Trailers", to: "/services/trailer-vin-verification" },
  { icon: Caravan, label: "RVs", to: "/services/rv-vin-verification" },
  { icon: CarFront, label: "Classic Cars", to: "/services/classic-car-vin-verification" },
];

export default function CityPage() {
  const { slug } = useParams();
  const city = cityBySlug(slug);

  const nearby = useMemo(() => {
    const sameCounty = CITIES.filter((c) => c.slug !== slug && c.county === city?.county);
    const otherAreas = CITIES.filter((c) => c.slug !== slug && c.county !== city?.county);
    return [...sameCounty, ...otherAreas];
  }, [slug, city]);
  if (!city) return <NotFound />;

  const title = `Mobile VIN Verification ${city.name}, CA | Same-Day`;
  const description = `Licensed mobile VIN verification in ${city.name}, CA. We come to your home or work, complete DMV REG 31, and offer same-day appointments. Call ${SITE.phone}.`;
  const path = `/vin-verification/${city.slug}`;

  const faqs = [
    { q: `Do you provide mobile VIN verification in ${city.name}?`, a: `Yes. We are a fully mobile, California DMV-licensed vehicle verifier. We come to any address in ${city.name} and throughout ${city.county} — your home, workplace, or storage lot — and complete your REG 31 on site.` },
    { q: `Where is the closest DMV to ${city.name}?`, a: `The ${city.dmv} is the DMV office most ${city.name} drivers use. Rather than wait in line there, most customers have us complete the VIN verification at their location and simply submit the finished REG 31 to the DMV.` },
    { q: `How much does VIN verification cost in ${city.name}?`, a: `Mobile service to ${city.name} is quoted based on vehicle type and travel distance. Call ${SITE.phone} for a fast, upfront quote; any travel fee is confirmed before you book. Dealer and fleet quotes are available.` },
    { q: `How soon can you come to ${city.name}?`, a: `We offer same-day and next-day appointments across ${city.name}. Call or text ${SITE.phone} and we'll give you an arrival window that fits your schedule.` },
  ];

  const schema = [
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/#areas" }, { name: `${city.name}`, path }]),
    localBusinessSchema({ areaServed: [{ "@type": "City", name: `${city.name}, California` }] }),
    serviceSchema({ name: `VIN Verification in ${city.name}`, description, path }),
    faqSchema(faqs),
  ];

  return (
    <SubLayout>
      <Seo title={title} description={description} path={path} schema={schema} />

      {/* Hero */}
      <section className="bg-gradient-to-b from-mist to-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 lg:py-14">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Service Areas", path: "/#areas" }, { name: city.name, path }]} />
          <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 text-brand px-3.5 py-1.5 text-xs font-semibold mt-6">
            <ShieldCheck size={14} /> California DMV Licensed · {city.county}
          </div>
          <h1 className="font-display font-extrabold text-ink tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
            Mobile VIN Verification in {city.name}, CA
          </h1>
          <p className="text-slate2 text-lg mt-4 leading-relaxed">{city.blurb}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={SITE.phoneHref} className="btn-accent rounded-full px-7 py-4 text-base font-semibold flex items-center justify-center gap-2"><Phone size={18} /> Call Now</a>
            <Link to="/book" className="btn-outline rounded-full px-7 py-4 text-base font-semibold flex items-center justify-center gap-2"><CalendarCheck size={18} /> Book Appointment</Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate2">
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-brand" /> Licensed & Bonded</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-brand" /> Same-Day Service</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-brand" /> We Come To You</span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-20 space-y-14">
        {/* Intro */}
        <section className="prose-none">
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">California VIN Verification for {city.name} Residents</h2>
          <div className="text-slate2 text-base leading-relaxed mt-4 space-y-4">
            <p>If you live in {city.name} and recently bought a vehicle out of state, inherited a vehicle, or need to register a trailer, motorcycle or RV, California likely requires a VIN verification (DMV form REG 31) before the vehicle can be titled and registered here. Instead of loading up and driving to a busy DMV office, The VIN Verifier CA brings a licensed, professional vehicle verifier directly to you anywhere in {city.name}.</p>
            <p>We serve every corner of {city.name} — including {city.neighborhoods.join(", ")} — and the surrounding {city.county} communities. Whether your vehicle is parked at home near {city.landmarks[0]}, at your office, or in a storage lot, we meet you there, inspect the vehicle, and complete the REG 31 form on the spot so it's accepted by the DMV the first time.</p>
          </div>
        </section>

        {/* Why us */}
        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">Why {city.name} Drivers Choose The VIN Verifier CA</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {[
              ["California DMV Licensed", `A state-licensed, bonded verifier — your ${city.name} paperwork is completed legally and correctly.`],
              ["We Come To You", `No driving across ${city.county} in traffic. We meet you anywhere in ${city.name}.`],
              ["Same-Day Appointments", `Need it today? We offer fast same-day and next-day service in ${city.name}.`],
              ["Every Vehicle Type", `Cars, motorcycles, trailers, RVs, classic and out-of-state vehicles.`],
            ].map(([t, d]) => (
              <div key={t} className="card-soft hover:!translate-y-0 p-6 flex gap-3">
                <Check size={20} className="text-brand shrink-0 mt-0.5" />
                <div><h3 className="font-display font-semibold text-ink">{t}</h3><p className="text-slate2 text-sm mt-1">{d}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* Local landmarks & DMV */}
        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">Local {city.name} Service & the Nearest DMV</h2>
          <div className="text-slate2 text-base leading-relaxed mt-4 space-y-4">
            <p>{city.name} is known for {city.landmarks.slice(0, 3).join(", ")} and more. Wherever you are in the city, we can reach you quickly. Many residents visit the <strong>{city.dmv}</strong> for registration services, but the VIN verification step itself does not have to happen there — a licensed verifier can complete it anywhere, which is exactly why our mobile service saves you so much time.</p>
            <p>Bring your completed REG 31 (which we hand you on the spot) to the {city.dmv} along with your title, smog certificate if required, and proof of insurance, and your registration visit will go far more smoothly.</p>
            <p>Appointments are available throughout {city.neighborhoods.join(", ")}. We verify eligible out-of-state cars, motorcycles, trailers, RVs, classic cars and commercial vehicles at homes, workplaces, dealerships and storage facilities across {city.name}.</p>
          </div>
        </section>

        {/* Vehicles */}
        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">Vehicles We Verify in {city.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
            {VEHICLE_LINKS.map((v) => {
              const Icon = v.icon;
              return (
                <Link key={v.label} to={v.to} className="card-soft p-5 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><Icon size={20} /></span>
                  <span className="font-display font-semibold text-ink text-sm">{v.label}</span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* How it works */}
        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">How VIN Verification Works in {city.name}</h2>
          <ol className="mt-6 space-y-4">
            {[
              [`Call or request a quote`, `Call ${SITE.phone} or request a quote online. Tell us your ${city.name} location and vehicle type.`],
              [`We schedule & come to you`, `We confirm a same-day or next-day arrival window and drive to your ${city.name} address.`],
              [`On-site inspection`, `We record the VIN from the dash, door jamb and federal label and verify it against your paperwork (about 15–20 minutes).`],
              [`Get your signed REG 31`, `We hand you the completed, signed REG 31 — ready to submit to the DMV.`],
            ].map(([t, d], i) => (
              <li key={t} className="flex gap-4">
                <span className="h-9 w-9 shrink-0 rounded-full bg-brand text-white font-display font-bold flex items-center justify-center">{i + 1}</span>
                <div><h3 className="font-display font-semibold text-ink">{t}</h3><p className="text-slate2 text-sm mt-1">{d}</p></div>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl">{city.name} VIN Verification FAQ</h2>
          <div className="mt-6 space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="card-soft hover:!translate-y-0 p-6">
                <h3 className="font-display font-semibold text-ink">{f.q}</h3>
                <p className="text-slate2 text-sm mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <CtaBlock heading={`Get your VIN verified in ${city.name} today`} sub={`Licensed, mobile, and same-day across ${city.county}. Call or text ${SITE.phone} for a fast quote.`} />

        {/* Nearby */}
        <section>
          <h2 className="font-display font-bold text-ink text-xl">Nearby Areas We Serve</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {nearby.map((c) => (
              <Link key={c.slug} to={`/vin-verification/${c.slug}`} className="inline-flex items-center rounded-full bg-mist border border-gray-100 text-slate2 text-sm px-3 py-1.5 hover:text-brand hover:border-brand/30 transition-colors">
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </SubLayout>
  );
}
